import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Receipt, Search, Undo2, Wallet } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { money } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
  type PageMeta,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { LOAN_STATUS } from '@/lib/status';
import { LoanDetailView, type LoanDetailResponse } from '@/pages/portal/LoanDetail';

interface LoanRow {
  id: string;
  loanNumber: string;
  memberName: string;
  membershipNumber: string;
  productName: string;
  principal: string;
  amountRepaid: string;
  outstanding: string;
  status: string;
  daysInArrears: number;
  finalDueDate: string;
}

export function AdminLoans() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-loans', page, query, status, overdueOnly],
    queryFn: () =>
      api.get<{ data: LoanRow[]; meta: PageMeta }>(
        `/loans?pageSize=25&page=${page}${query ? `&q=${encodeURIComponent(query)}` : ''}${
          status ? `&status=${status}` : ''
        }${overdueOnly ? '&overdueOnly=true' : ''}`,
      ),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Loan operations"
        title="Loans"
        description={data ? `${data.meta.total} loans` : undefined}
      />

      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <Field label="Search" htmlFor="q" className="min-w-[220px] flex-1">
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-secondary"
              />
              <Input
                id="q"
                className="pl-9"
                placeholder="Loan number, name or membership number"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </div>
          </Field>

          <Field label="Status" htmlFor="status" className="w-52">
            <Select id="status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {Object.entries(LOAN_STATUS).map(([key, presentation]) => (
                <option key={key} value={key}>
                  {presentation.label}
                </option>
              ))}
            </Select>
          </Field>

          <label className="flex h-10 cursor-pointer items-center gap-2 text-sm font-semibold text-foreground">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
              className="h-4 w-4 accent-[hsl(var(--danger))]"
            />
            In arrears only
          </label>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState icon={<Wallet size={20} />} title="No loans match those filters" />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Loan</Th>
                  <Th>Member</Th>
                  <Th>Product</Th>
                  <Th numeric>Principal</Th>
                  <Th numeric>Repaid</Th>
                  <Th numeric>Outstanding</Th>
                  <Th numeric>Arrears</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((loan) => (
                  <tr key={loan.id} className="transition-colors hover:bg-muted/50">
                    <Td>
                      <Link
                        to={`/admin/loans/${loan.id}`}
                        className="numeric font-semibold text-primary hover:underline"
                      >
                        {loan.loanNumber}
                      </Link>
                    </Td>
                    <Td>
                      <p className="font-medium text-foreground">{loan.memberName}</p>
                      <p className="numeric text-xs text-foreground-secondary">
                        {loan.membershipNumber}
                      </p>
                    </Td>
                    <Td className="text-foreground-secondary">{loan.productName}</Td>
                    <Td numeric>{money(loan.principal)}</Td>
                    <Td numeric className="text-success">
                      {money(loan.amountRepaid)}
                    </Td>
                    <Td numeric className="font-semibold">
                      {money(loan.outstanding)}
                    </Td>
                    <Td numeric className={loan.daysInArrears > 0 ? 'font-bold text-danger' : ''}>
                      {loan.daysInArrears > 0 ? `${loan.daysInArrears}d` : '—'}
                    </Td>
                    <Td>
                      <StatusBadge map={LOAN_STATUS} status={loan.status} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function AdminLoanDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-loan', id],
    queryFn: () => api.get<LoanDetailResponse>(`/loans/${id}`),
    enabled: Boolean(id),
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) return null;

  const settled = data.status === 'COMPLETED';

  return (
    <div>
      <div className="mb-4 flex justify-end gap-2">
        {!settled && data.status !== 'PENDING_DISBURSEMENT' && (
          <Button onClick={() => setRecording(true)}>
            <Receipt size={16} /> Record repayment
          </Button>
        )}
      </div>

      <LoanDetailView loan={data} admin />

      {recording && (
        <RecordRepayment
          loan={data}
          onClose={() => setRecording(false)}
          onDone={() => {
            setRecording(false);
            void queryClient.invalidateQueries({ queryKey: ['admin-loan', id] });
            void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
          }}
        />
      )}
    </div>
  );
}

function RecordRepayment({
  loan,
  onClose,
  onDone,
}: {
  loan: LoanDetailResponse;
  onClose: () => void;
  onDone: () => void;
}) {
  const nextAmount = loan.nextPayment ? (Number(loan.nextPayment.amount) / 100).toFixed(2) : '';

  const [amount, setAmount] = useState(nextAmount);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const record = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/repayments', {
        loanId: loan.id,
        amount,
        paymentDate,
        paymentMethod: method,
        ...(reference ? { transactionReference: reference } : {}),
        ...(notes ? { notes } : {}),
      }),
    onSuccess: (result) => {
      toast.success(result.message);
      onDone();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/50 p-0 sm:items-center sm:p-6">
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <CardHeader>
          <CardTitle>Record repayment — {loan.loanNumber}</CardTitle>
          <p className="text-sm text-foreground-secondary">
            Outstanding: <span className="numeric font-semibold">{money(loan.outstanding)}</span>
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          <Field label="Amount (₦)" htmlFor="amount" required>
            <Input
              id="amount"
              className="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            />
          </Field>

          <Field label="Payment date" htmlFor="paymentDate" required hint="Cannot be in the future.">
            <Input
              id="paymentDate"
              type="date"
              max={new Date().toISOString().slice(0, 10)}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </Field>

          <Field label="Payment method" htmlFor="method" required>
            <Select id="method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {['BANK_TRANSFER', 'CASH', 'DIRECT_DEBIT', 'SALARY_DEDUCTION', 'CHEQUE', 'ONLINE', 'OTHER'].map(
                (option) => (
                  <option key={option} value={option}>
                    {option.replace(/_/g, ' ')}
                  </option>
                ),
              )}
            </Select>
          </Field>

          <Field
            label="Transaction reference"
            htmlFor="reference"
            hint="The bank reference. Recording the same reference twice is rejected."
          >
            <Input id="reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>

          <Field label="Notes" htmlFor="notes">
            <Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          <div className="flex gap-3 border-t border-border pt-4">
            <Button full loading={record.isPending} disabled={!amount} onClick={() => record.mutate()}>
              Record payment
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>

          <p className="flex items-start gap-1.5 text-xs text-foreground-secondary">
            <Undo2 size={12} className="mt-0.5 shrink-0" />
            Payments are never deleted. If this is wrong, it can be reversed — the original record
            stays on the ledger with a reason.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
